import React, { useState, useEffect } from 'react';
import { db } from '../services/firebaseService';
import { doc, getDoc, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { transformPaymentPeriodLabel } from '../utils/orderUtils';

export default function DraftAgreementPage() {
  const { isAuthenticated, currentAgent } = useAuth();
  const [draftOrders, setDraftOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [agreementHTML, setAgreementHTML] = useState(null);
  const [receiptHTML, setReceiptHTML] = useState(null);
  const [activeTab, setActiveTab] = useState('agreement');

  useEffect(() => {
    const fetchDraftOrders = async () => {
      try {
        console.log('🔍 Starting fetchDraftOrders...');
        setLoading(true);
        setError(null);
        
        const q = query(
          collection(db, 'draft_orders'),
          orderBy('created_at', 'desc'),
          limit(50)
        );
        
        console.log('📋 Query created, executing...');
        const querySnapshot = await getDocs(q);
        console.log(`📊 Found ${querySnapshot.size} documents`);
        
        const orders = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          console.log('📄 Processing document:', doc.id, data);
          orders.push({
            id: doc.id,
            ...data
          });
        });
        
        console.log('✅ Setting draft orders:', orders);
        setDraftOrders(orders);
        setLoading(false);
      } catch (err) {
        console.error('❌ Error fetching draft orders:', err);
        setError('Failed to load draft orders');
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      console.log('🔐 User authenticated, fetching draft orders...');
      fetchDraftOrders();
    } else {
      console.log('🚫 User not authenticated');
    }
  }, [isAuthenticated]);

  const handleOrderSelect = async (orderId) => {
    try {
      console.log('🎯 Selecting order:', orderId);
      setLoading(true);
      setError(null);
      setAgreementHTML(null);
      setReceiptHTML(null);
      
      const orderRef = doc(db, 'draft_orders', orderId);
      const orderDoc = await getDoc(orderRef);
      
      if (orderDoc.exists()) {
        const orderData = orderDoc.data();
        console.log('📋 Order data retrieved:', orderData);
        setSelectedOrder(orderData);
        
        // Generate agreement HTML for preview
        try {
          const html = await buildAgreementHTML(orderData);
          setAgreementHTML(html);
          console.log('✅ Agreement HTML generated for preview');
        } catch (htmlErr) {
          console.error('❌ Failed to generate preview HTML:', htmlErr);
          setAgreementHTML(null);
        }
        
        // Generate receipt HTML for preview
        try {
          const receipt = await buildReceiptHTML(orderData);
          setReceiptHTML(receipt);
          console.log('✅ Receipt HTML generated for preview');
        } catch (receiptErr) {
          console.error('❌ Failed to generate receipt HTML:', receiptErr);
          setReceiptHTML(null);
        }
        
        setLoading(false);
      } else {
        console.log('❌ Order not found:', orderId);
        setError('Draft order not found');
        setLoading(false);
      }
    } catch (err) {
      console.error('❌ Error fetching order:', err);
      setError('Failed to load draft order');
      setLoading(false);
    }
  };

  const generateReceiptPDF = async () => {
    if (!selectedOrder) return;
    
    try {
      setGenerating(true);
      setError(null);
      
      console.log('🧾 Starting receipt PDF generation for:', selectedOrder.code);
      
      // Generate receipt HTML content
      const html = await buildReceiptHTML(selectedOrder);
      console.log('✅ Receipt HTML generated, length:', html.length);
      
      // Detect mobile device
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      console.log('📱 Is mobile:', isMobile);
      
      if (isMobile) {
        // Mobile approach: Direct download
        console.log('📱 Using mobile approach for receipt...');
        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = `receipt-${selectedOrder.code}.html`;
        link.style.display = 'none';
        document.body.appendChild(link);
        
        try {
          link.click();
          console.log('✅ Mobile receipt download triggered');
        } catch (err) {
          console.error('❌ Mobile receipt download failed:', err);
          window.open(url, '_blank');
        }
        
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }, 100);
        
        setGenerating(false);
        console.log('✅ Mobile receipt process completed');
        
      } else {
        // Desktop approach: Print window
        console.log('🖥️ Using desktop approach for receipt...');
        const printWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
        
        if (printWindow) {
          console.log('✅ Receipt print window opened');
          printWindow.document.write(html);
          printWindow.document.close();
          
          const checkLoaded = () => {
            try {
              if (printWindow.document.readyState === 'complete') {
                console.log('✅ Receipt print window content loaded');
                setTimeout(() => {
                  try {
                    printWindow.print();
                    console.log('✅ Receipt print dialog opened');
                    setTimeout(() => {
                      printWindow.close();
                      setGenerating(false);
                      console.log('✅ Receipt print window closed');
                    }, 5000);
                  } catch (printErr) {
                    console.error('❌ Receipt print failed:', printErr);
                    printWindow.close();
                    setGenerating(false);
                  }
                }, 500);
              } else {
                setTimeout(checkLoaded, 100);
              }
            } catch (err) {
              console.error('❌ Receipt print window error:', err);
              printWindow.close();
              setGenerating(false);
            }
          };
          
          checkLoaded();
          
        } else {
          // Fallback: Popup blocked
          console.log('🔄 Fallback: Popup blocked for receipt, creating blob...');
          const blob = new Blob([html], { type: 'text/html' });
          const url = URL.createObjectURL(blob);
          
          const link = document.createElement('a');
          link.href = url;
          link.download = `receipt-${selectedOrder.code}.html`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          
          setGenerating(false);
          console.log('✅ Fallback receipt HTML file downloaded');
        }
      }
      
    } catch (err) {
      console.error('❌ Error generating receipt PDF:', err);
      setError('Failed to generate receipt: ' + err.message);
      setGenerating(false);
    }
  };

  const generateAgreementPDF = async () => {
    if (!selectedOrder) return;
    
    try {
      setGenerating(true);
      setError(null);
      
      console.log('📄 Starting PDF generation for:', selectedOrder.code);
      console.log('📱 User Agent:', navigator.userAgent);
      console.log('🖥️ Screen size:', window.innerWidth + 'x' + window.innerHeight);
      
      // Generate HTML content (now async to handle base64 image)
      const html = await buildAgreementHTML(selectedOrder);
      console.log('✅ HTML generated, length:', html.length);
      
      // Detect mobile device
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      console.log('📱 Is mobile:', isMobile);
      
      if (isMobile) {
        // Mobile approach: Direct download
        console.log('📱 Using mobile approach...');
        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = `agreement-${selectedOrder.code}.html`;
        link.style.display = 'none';
        document.body.appendChild(link);
        
        // Try to trigger download
        try {
          link.click();
          console.log('✅ Mobile download triggered');
        } catch (err) {
          console.error('❌ Mobile download failed:', err);
          // Fallback: Open in new tab
          window.open(url, '_blank');
        }
        
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }, 100);
        
        setGenerating(false);
        console.log('✅ Mobile process completed');
        
      } else {
        // Desktop approach: Print window
        console.log('🖥️ Using desktop approach...');
        const printWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
        
        if (printWindow) {
          console.log('✅ Print window opened');
          printWindow.document.write(html);
          printWindow.document.close();
          
          // Wait for content to load, then trigger print automatically
          const checkLoaded = () => {
            try {
              if (printWindow.document.readyState === 'complete') {
                console.log('✅ Print window content loaded');
                setTimeout(() => {
                  try {
                    printWindow.print();
                    console.log('✅ Print dialog opened');
                    // Don't close immediately - let user handle print dialog
                    setTimeout(() => {
                      printWindow.close();
                      setGenerating(false);
                      console.log('✅ Print window closed');
                    }, 5000); // Close after 5 seconds
                  } catch (printErr) {
                    console.error('❌ Print failed:', printErr);
                    printWindow.close();
                    setGenerating(false);
                  }
                }, 500); // Reduced delay for faster print
              } else {
                setTimeout(checkLoaded, 100);
              }
            } catch (err) {
              console.error('❌ Print window error:', err);
              printWindow.close();
              setGenerating(false);
            }
          };
          
          checkLoaded();
          
        } else {
          // Fallback: Popup blocked
          console.log('🔄 Fallback: Popup blocked, creating blob...');
          const blob = new Blob([html], { type: 'text/html' });
          const url = URL.createObjectURL(blob);
          
          const link = document.createElement('a');
          link.href = url;
          link.download = `agreement-${selectedOrder.code}.html`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          
          setGenerating(false);
          console.log('✅ Fallback HTML file downloaded');
        }
      }
      
    } catch (err) {
      console.error('❌ Error generating PDF:', err);
      setError('Failed to generate agreement: ' + err.message);
      setGenerating(false);
    }
  };

  const buildReceiptHTML = async (order) => {
    // Helper functions
    const formatDate = (dateValue) => {
      const date = new Date(dateValue?.toDate?.() || dateValue);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(' ', '-');
    };

    // Get header image as base64 (same as agreement)
    const getHeaderImage = async (country) => {
      const countryMap = {
        'Bahrain': 'header-bh.png',
        'United Arab Emirates': 'header-ae.png',
        'Saudi Arabia': 'header-sa.png',
        'Kuwait': 'header-kw.png',
        'Qatar': 'header-qa.png',
        'Oman': 'header-om.png',
        'Philippines': 'header-ph.png'
      };
      
      const countryCode = countryMap[country] || 'header-default.png';
      const imageUrl = `https://agents.plsss.vip/images/${countryCode}`;
      
      try {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.warn('Failed to load header image:', error);
        return null;
      }
    };

    // Get header image
    const headerImageSrc = await getHeaderImage(order.customer?.country);

    const getPaymentPlanDetails = (paymentPeriod) => {
      const plans = {
        'day1': { label: 'Pay full within 2 days', type: 'Pay full within 2 days', frequency: 'One-time', amount: null, installments: 0, nextDate: null, finalDate: null },
        'day30': { label: 'Pay full by end of the month', type: 'Pay full by end of the month', frequency: 'Monthly', amount: null, installments: 1, nextDate: null, finalDate: null },
        'day60': { label: 'Pay full within 2 months', type: 'Pay full within 2 months', frequency: 'Monthly', amount: null, installments: 2, nextDate: null, finalDate: null },
        'day90': { label: 'Pay full within 3 months', type: 'Pay full within 3 months', frequency: 'Monthly', amount: null, installments: 3, nextDate: null, finalDate: null },
        'day180': { label: 'Pay full within 6 months', type: 'Pay full within 6 months', frequency: 'Monthly', amount: null, installments: 6, nextDate: null, finalDate: null }
      };
      
      const plan = plans[paymentPeriod] || plans['day1'];
      
      // Calculate installment dates and amounts
      if (paymentPeriod === 'day60' || paymentPeriod === 'day90' || paymentPeriod === 'day180') {
        const totalDue = Number(order.payment?.total_amount_local_currency || 0);
        const paid = Number(order.payment?.amount_paid_local_currency || 0);
        const remaining = totalDue - paid;
        const installmentAmount = remaining / plan.installments;
        
        const orderDate = new Date(order.created_at?.toDate?.() || order.created_at);
        const nextDate = new Date(orderDate);
        const finalDate = new Date(orderDate);
        
        if (paymentPeriod === 'day60') {
          nextDate.setDate(nextDate.getDate() + 30);
          finalDate.setDate(finalDate.getDate() + 60);
        } else if (paymentPeriod === 'day90') {
          nextDate.setDate(nextDate.getDate() + 30);
          finalDate.setDate(finalDate.getDate() + 90);
        } else if (paymentPeriod === 'day180') {
          nextDate.setDate(nextDate.getDate() + 30);
          finalDate.setDate(finalDate.getDate() + 180);
        }
        
        plan.amount = installmentAmount;
        plan.nextDate = formatDate(nextDate);
        plan.finalDate = formatDate(finalDate);
      }
      
      return plan;
    };

    // Calculate totals
    const subtotal = Number(order.subtotal_local || 0);
    const adminFee = subtotal * 0.05; // 5% admin fee
    const vat = (subtotal + adminFee) * 0.10; // 10% VAT
    const grandTotal = subtotal + adminFee + vat;

    // Get payment plan details
    const paymentPlan = getPaymentPlanDetails(order.paymentPeriod);

    // Generate order items rows
    const itemsRows = order.items?.map(i => `
      <tr>
        <td>${i.sku || 'N/A'}</td>
        <td>${i.name || 'N/A'}</td>
        <td>${order.currency} ${Number(i.unit_cost_local_currency || 0).toFixed(2)}</td>
        <td>${Number(i.quantity || 0)}</td>
        <td>${order.currency} ${Number(i.subtotal_local_currency || 0).toFixed(2)}</td>
      </tr>
    `).join("") || '';

    const paymentPlanSection = order.paymentPeriod && paymentPlan.installments > 0 ? `
        <div class="section">
          <div class="section-title">Payment Plan Details</div>
          <table>
            <thead>
              <tr>
                <th>Payment Plan Type</th>
                <th>Instalment Frequency</th>
                <th>Instalment Amount</th>
                <th>No of Instalments</th>
                <th>Next Instalment Date</th>
                <th>Final Instalment Date</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>${paymentPlan.type}</td>
                <td>${paymentPlan.frequency}</td>
                <td>${order.currency} ${paymentPlan.amount ? paymentPlan.amount.toFixed(2) : '0.00'}</td>
                <td>${paymentPlan.installments}</td>
                <td>${paymentPlan.nextDate || 'N/A'}</td>
                <td>${paymentPlan.finalDate || 'N/A'}</td>
              </tr>
            </tbody>
          </table>
        </div>
        ` : '';

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Order Receipt - ${order.code}</title>
        <style>
          @page { margin: 15mm; }
          body { 
            font-family: Arial, sans-serif; 
            font-size: 11px; 
            line-height: 1.2; 
            margin: 0; 
            padding: 10px;
            color: #000;
          }
          .header { 
            text-align: center; 
            margin-bottom: 15px; 
          }
          .header-image { 
            max-width: 250px; 
            height: auto; 
            margin-bottom: 10px;
          }
          .header-text { 
            font-weight: bold; 
            font-size: 14px; 
            color: #2c3e50;
            margin-bottom: 5px;
          }
          .section { 
            margin-bottom: 12px; 
          }
          .section-title { 
            font-weight: bold; 
            font-size: 12px; 
            margin-bottom: 6px; 
            border-bottom: 1px solid #ccc; 
            padding-bottom: 3px; 
          }
          .two-column { 
            display: flex; 
            gap: 15px; 
            margin-bottom: 12px; 
          }
          .column { 
            flex: 1; 
          }
          .field-row { 
            display: flex; 
            margin-bottom: 3px; 
          }
          .field-label { 
            font-weight: bold; 
            width: 100px; 
            font-size: 10px;
          }
          .field-value { 
            flex: 1; 
            font-size: 10px;
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-bottom: 12px; 
          }
          th, td { 
            border: 1px solid #000; 
            padding: 3px; 
            text-align: left; 
            vertical-align: top; 
            font-size: 9px;
          }
          th { 
            background-color: #f0f0f0; 
            font-weight: bold; 
            text-align: center; 
            font-size: 9px;
          }
          .totals { 
            text-align: right; 
            font-weight: bold; 
          }
          .terms { 
            margin-top: 15px; 
            font-size: 8px; 
          }
          .terms ol { 
            margin: 5px 0; 
            padding-left: 15px; 
          }
          .signature { 
            margin-top: 20px; 
            display: flex; 
            gap: 30px; 
          }
          .signature-line { 
            border-bottom: 1px solid #000; 
            width: 150px; 
            margin-bottom: 3px; 
          }
          .signature-box {
            font-size: 9px;
          }
          @media print {
            body { margin: 10px; }
            .header { page-break-after: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          ${headerImageSrc ? `<img src="${headerImageSrc}" alt="PLSSS Header" class="header-image" />` : '<div class="header-text">PLSSS</div>'}
          <div class="header-text">ORDER RECEIPT</div>
          <div style="font-size: 10px; margin-top: 3px;">
            <strong>Order Code:</strong> ${order.code} | <strong>Date:</strong> ${formatDate(order.created_at)}
          </div>
        </div>

        <div class="two-column">
          <div class="column">
            <div class="section">
              <div class="section-title">Customer Details</div>
              <div class="field-row">
                <div class="field-label">Name:</div>
                <div class="field-value">${order.customer?.name || 'N/A'}</div>
              </div>
              <div class="field-row">
                <div class="field-label">Govt ID:</div>
                <div class="field-value">${order.customer?.govt_id || 'N/A'}</div>
              </div>
              <div class="field-row">
                <div class="field-label">Email:</div>
                <div class="field-value">${order.customer?.email || 'N/A'}</div>
              </div>
              <div class="field-row">
                <div class="field-label">Mobile:</div>
                <div class="field-value">${order.customer?.mobile || 'N/A'}</div>
              </div>
              <div class="field-row">
                <div class="field-label">Address:</div>
                <div class="field-value">${order.customer?.shipping_address || order.customer?.billing_address || 'N/A'}</div>
              </div>
            </div>
          </div>

          <div class="column">
            <div class="section">
              <div class="section-title">Payment Details</div>
              <div class="field-row">
                <div class="field-label">Method:</div>
                <div class="field-value">${order.payment?.method || 'CASH'}</div>
              </div>
              <div class="field-row">
                <div class="field-label">Date:</div>
                <div class="field-value">${formatDate(order.created_at)}</div>
              </div>
              <div class="field-row">
                <div class="field-label">Amount:</div>
                <div class="field-value">${order.currency} ${Number(order.payment?.amount_paid_local_currency || 0).toFixed(2)}</div>
              </div>
              <div class="field-row">
                <div class="field-label">Reference:</div>
                <div class="field-value">${order.payment?.reference || 'Cash'}</div>
              </div>
              <div class="field-row">
                <div class="field-label">Type:</div>
                <div class="field-value">${order.paymentPeriod ? 'Payment Plan' : 'Full Payment'}</div>
              </div>
              <div class="field-row">
                <div class="field-label">Total Due:</div>
                <div class="field-value">${order.currency} ${Number(order.payment?.total_amount_local_currency || 0).toFixed(2)}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Order Items</div>
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Description</th>
                <th>Unit Price</th>
                <th>Qty</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${itemsRows}
              <tr class="totals">
                <td colspan="4">Sub Total</td>
                <td>${order.currency} ${subtotal.toFixed(2)}</td>
              </tr>
              <tr class="totals">
                <td colspan="4">Admin Fee</td>
                <td>${order.currency} ${adminFee.toFixed(2)}</td>
              </tr>
              <tr class="totals">
                <td colspan="4">Vat (10%)</td>
                <td>${order.currency} ${vat.toFixed(2)}</td>
              </tr>
              <tr class="totals">
                <td colspan="4">Grand Total</td>
                <td>${order.currency} ${grandTotal.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        ${paymentPlanSection}

        <div class="terms">
          <div class="section-title">Terms and Conditions:</div>
          <ol>
            <li>Gift items will be delivered upon final payment completion. Failure to complete payments by the final date will result in forfeiture of all gift items.</li>
            <li>Purchased items will be delivered in stages starting with immediate delivery of the Platinum Roaster. Remaining items will be delivered proportionally to payment amounts received.</li>
          </ol>
        </div>

        <div class="signature">
          <div class="signature-box">
            <div class="signature-line"></div>
            <div>Customer Signature</div>
          </div>
          <div class="signature-box">
            <div class="signature-line"></div>
            <div>Date</div>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const buildAgreementHTML = async (order) => {
    // Helper functions
    const formatDate = (dateValue) => {
      const date = new Date(dateValue?.toDate?.() || dateValue);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    // Get header image as base64
    const getHeaderImage = async (country) => {
      const countryMap = {
        'Bahrain': 'header-bh.png',
        'United Arab Emirates': 'header-ae.png',
        'Saudi Arabia': 'header-sa.png',
        'Kuwait': 'header-kw.png',
        'Qatar': 'header-qa.png',
        'Oman': 'header-om.png',
        'Philippines': 'header-ph.png'
      };
      
      const countryCode = countryMap[country] || 'header-default.png';
      const imageUrl = `https://agents.plsss.vip/images/${countryCode}`;
      
      try {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.warn('Failed to load header image:', error);
        return null;
      }
    };

    // Get header image
    const headerImageSrc = await getHeaderImage(order.customer?.country);
    
    // Generate items table rows
    const itemsRows = order.items?.map(i => `
      <tr>
        <td>${i.name}</td>
        <td>${i.sku}</td>
        <td>${Number(i.quantity)}</td>
        <td>${i.is_gift === "true" ? "🎁 GIFT" : order.currency + " " + Number(i.unit_cost_local_currency || 0).toLocaleString()}</td>
        <td>${i.is_gift === "true" ? "-" : order.currency + " " + Number(i.subtotal_local_currency || 0).toLocaleString()}</td>
      </tr>
    `).join("") || '';

    // Helper functions for payment plan
    const getPlanType = (period) => {
      const types = {
        'day1': 'Pay full within 2 days',
        'day30': 'Pay full by end of the month',
        'day60': 'Pay full within 2 months',
        'day90': 'Pay full within 3 months',
        'day180': 'Pay full within 6 months'
      };
      return types[period] || 'Not Specified';
    };

    const getFrequency = (period) => {
      const frequencies = {
        'day1': 'One-time',
        'day30': 'Monthly',
        'day60': 'Monthly',
        'day90': 'Monthly',
        'day180': 'Monthly'
      };
      return frequencies[period] || 'Not Specified';
    };

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Purchase Agreement - ${order.code}</title>
        <style>
          body { 
            font-family: Arial, sans-serif; 
            line-height: 1.6; 
            margin: 40px; 
            color: #333;
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
          }
          .header { 
            text-align: center; 
            margin-bottom: 40px; 
          }
          .header-image { 
            max-width: 300px; 
            height: auto; 
            margin-bottom: 20px;
          }
          .header-text { 
            font-size: 24px; 
            font-weight: bold; 
            color: #2c3e50;
          }
          .section { 
            margin-bottom: 30px; 
          }
          .section-title { 
            font-size: 18px; 
            font-weight: bold; 
            margin-bottom: 15px; 
            color: #2c3e50;
            border-bottom: 2px solid #3498db;
            padding-bottom: 5px;
          }
          .info-grid { 
            display: grid; 
            grid-template-columns: 1fr 1fr; 
            gap: 20px; 
            margin-bottom: 20px; 
          }
          .info-item { 
            margin-bottom: 10px; 
          }
          .info-label { 
            font-weight: bold; 
            color: #555;
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin: 20px 0; 
          }
          th, td { 
            border: 1px solid #ddd; 
            padding: 12px; 
            text-align: left; 
          }
          th { 
            background-color: #f8f9fa; 
            font-weight: bold; 
          }
          .terms { 
            background-color: #f8f9fa; 
            padding: 20px; 
            border-radius: 5px; 
            margin: 20px 0; 
          }
          .terms ol { 
            margin: 0; 
            padding-left: 20px; 
          }
          .terms li { 
            margin-bottom: 10px; 
          }
          .signature-area { 
            margin-top: 50px; 
            display: flex; 
            justify-content: space-between; 
          }
          .signature-box { 
            width: 45%; 
            text-align: center; 
          }
          .signature-line { 
            border-bottom: 1px solid #333; 
            margin: 10px 0; 
            height: 40px; 
          }
          .footer { 
            text-align: center; 
            margin-top: 40px; 
            font-size: 12px; 
            color: #666; 
          }
          @media print {
            body { margin: 20px; }
            .header { page-break-after: always; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          ${headerImageSrc ? `<img src="${headerImageSrc}" alt="PLSSS Header" class="header-image" />` : '<div class="header-text">PLSSS Order Agreement</div>'}
          <h1>Purchase Agreement</h1>
          <p><strong>Order Code:</strong> ${order.code}</p>
          <p><strong>Date:</strong> ${formatDate(order.created_at)}</p>
        </div>

        <div class="section">
          <h2 class="section-title">Customer Information</h2>
          <div class="info-grid">
            <div>
              <div class="info-item">
                <span class="info-label">Name:</span> ${order.customer?.name || 'N/A'}
              </div>
              <div class="info-item">
                <span class="info-label">Email:</span> ${order.customer?.email || 'N/A'}
              </div>
              <div class="info-item">
                <span class="info-label">Phone:</span> ${order.customer?.mobile || 'N/A'}
              </div>
            </div>
            <div>
              <div class="info-item">
                <span class="info-label">Country:</span> ${order.customer?.country || 'N/A'}
              </div>
              <div class="info-item">
                <span class="info-label">Government ID:</span> ${order.customer?.govt_id || 'N/A'}
              </div>
              <div class="info-item">
                <span class="info-label">Shipping Address:</span> ${order.customer?.shipping_address || order.customer?.billing_address || 'N/A'}
              </div>
            </div>
          </div>
        </div>

        <div class="section">
          <h2 class="section-title">Order Details</h2>
          <table>
            <thead>
              <tr>
                <th>Product Name</th>
                <th>SKU</th>
                <th>Quantity</th>
                <th>Unit Price</th>
                <th>Total Price</th>
              </tr>
            </thead>
            <tbody>
              ${itemsRows}
            </tbody>
          </table>
          
          <div class="info-grid">
            <div>
              <div class="info-item">
                <span class="info-label">Subtotal:</span> ${order.currency} ${Number(order.subtotal_local || 0).toLocaleString()}
              </div>
              <div class="info-item">
                <span class="info-label">Total Amount:</span> ${order.currency} ${Number(order.payment?.total_amount_local_currency || 0).toLocaleString()}
              </div>
            </div>
            <div>
              <div class="info-item">
                <span class="info-label">Amount Paid:</span> ${order.currency} ${Number(order.payment?.amount_paid_local_currency || 0).toLocaleString()}
              </div>
              <div class="info-item">
                <span class="info-label">Balance Due:</span> ${order.currency} ${(Number(order.payment?.total_amount_local_currency || 0) - Number(order.payment?.amount_paid_local_currency || 0)).toLocaleString()}
              </div>
            </div>
          </div>
        </div>

        ${order.paymentPeriod ? `
        <div class="section">
          <h2 class="section-title">Payment Plan</h2>
          <table>
            <tr>
              <td><strong>Payment Period:</strong></td>
              <td>${transformPaymentPeriodLabel(order.paymentPeriod)}</td>
              <td><strong>Plan Type:</strong></td>
              <td>${getPlanType(order.paymentPeriod)}</td>
            </tr>
            <tr>
              <td><strong>Payment Frequency:</strong></td>
              <td>${getFrequency(order.paymentPeriod)}</td>
              <td><strong>Initial Payment:</strong></td>
              <td>${order.currency} ${Number(order.payment?.amount_paid_local_currency || 0).toLocaleString()}</td>
            </tr>
            <tr>
              <td><strong>Remaining Amount:</strong></td>
              <td>${order.currency} ${(Number(order.payment?.total_amount_local_currency || 0) - Number(order.payment?.amount_paid_local_currency || 0)).toLocaleString()}</td>
              <td><strong>Payment Schedule:</strong></td>
              <td>Based on selected payment period</td>
            </tr>
          </table>
        </div>
        ` : ''}

        <div class="section">
          <h2 class="section-title">Payment Summary</h2>
          <table>
            <tr><td><strong>Subtotal:</strong></td><td>${order.currency} ${Number(order.subtotal_local || 0).toLocaleString()}</td></tr>
            <tr><td><strong>Total Amount:</strong></td><td>${order.currency} ${Number(order.payment?.total_amount_local_currency || 0).toLocaleString()}</td></tr>
            <tr><td><strong>Amount Paid:</strong></td><td>${order.currency} ${Number(order.payment?.amount_paid_local_currency || 0).toLocaleString()}</td></tr>
            <tr><td><strong>Balance Due:</strong></td><td>${order.currency} ${(Number(order.payment?.total_amount_local_currency || 0) - Number(order.payment?.amount_paid_local_currency || 0)).toLocaleString()}</td></tr>
          </table>
        </div>

        <div class="section">
          <h2 class="section-title">Terms and Conditions</h2>
          <div class="terms">
            <ol>
              <li>This agreement constitutes a legally binding contract between PLSSS and the customer named above.</li>
              <li>All payments must be completed according to the specified payment schedule.</li>
              <li>Products will be delivered as per the delivery schedule outlined in the payment plan.</li>
              <li>Gift items will be delivered upon completion of all payments as specified.</li>
              <li>The customer agrees to all terms and conditions outlined in this agreement.</li>
              <li>Any breach of this agreement may result in legal action and forfeiture of benefits.</li>
            </ol>
          </div>
        </div>

        <div class="signature-area">
          <div class="signature-box">
            <h3>Customer Signature</h3>
            <div class="signature-line"></div>
            <p>${order.customer?.name || 'Customer Name'}</p>
            <p>Date: _________________</p>
          </div>
          <div class="signature-box">
            <h3>PLSSS Representative</h3>
            <div class="signature-line"></div>
            <p>Authorized Signature</p>
            <p>Date: _________________</p>
          </div>
        </div>

        <div class="footer">
          <p>© 2026 PLSSS. All rights reserved.</p>
          <p>This agreement is electronically generated and valid upon signature.</p>
        </div>
      </body>
      </html>
    `;
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Authentication Required</h2>
          <p className="text-gray-600">Please log in to access draft agreements.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="w-full max-w-full px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Draft Agreements</h1>
          <p className="text-gray-600">Generate and print agreements from draft orders</p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Draft Orders List */}
          <div className="w-full lg:w-96 xl:w-1/3 bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Draft Orders</h2>
            {draftOrders.length === 0 ? (
              <p className="text-gray-500">No draft orders found</p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {draftOrders.map((order) => (
                  <div 
                    key={order.id}
                    className={`p-3 sm:p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                      selectedOrder?.id === order.id 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    onClick={() => handleOrderSelect(order.id)}
                  >
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-base sm:text-lg">{order.code}</h3>
                        <p className="text-sm text-gray-600">{order.customer?.name}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(order.created_at?.toDate?.() || order.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className={`px-3 py-1 rounded text-sm font-medium shrink-0 ${
                        order.orderStatus === 'DRAFT' ? 'bg-yellow-100 text-yellow-800' :
                        order.orderStatus === 'ACCEPTED' ? 'bg-green-100 text-green-800' :
                        order.orderStatus === 'CONFIRMED' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {order.orderStatus || 'DRAFT'}
                      </div>
                    </div>
                    <div className="text-sm text-gray-600 mt-2 sm:mt-0 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <p><strong>Payment:</strong> {order.paymentPeriod || 'Immediate'}</p>
                      <p><strong>Total:</strong> {order.currency} {Number(order.payment?.total_amount_local_currency || 0).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Agreement Preview */}
          <div className="flex-1 bg-white rounded-lg shadow p-6 min-h-0">
            <h2 className="text-xl font-semibold mb-4">Document Preview</h2>
            {selectedOrder ? (
              <div>
                <div className="mb-4">
                  <h3 className="text-lg font-medium mb-3">Documents for {selectedOrder.code}</h3>
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                    <button 
                      onClick={generateReceiptPDF}
                      disabled={generating}
                      className="btn bg-green-600 text-white flex items-center justify-center gap-2 w-full sm:w-auto"
                    >
                      {generating ? (
                        <>
                          <LoadingSpinner size="sm" />
                          <span>Generating...</span>
                        </>
                      ) : (
                        <>
                          🧾 Receipt
                        </>
                      )}
                    </button>
                    <button 
                      onClick={generateAgreementPDF}
                      disabled={generating}
                      className="btn btn-primary flex items-center justify-center gap-2 w-full sm:w-auto"
                    >
                      {generating ? (
                        <>
                          <LoadingSpinner size="sm" />
                          <span>Generating...</span>
                        </>
                      ) : (
                        <>
                          🖨️ Agreement
                        </>
                      )}
                    </button>
                  </div>
                </div>
                
                {/* Tabs */}
                <div className="border-b border-gray-200 mb-4">
                  <nav className="-mb-px flex space-x-8">
                    <button
                      onClick={() => setActiveTab('agreement')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'agreement'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      📄 Agreement
                    </button>
                    <button
                      onClick={() => setActiveTab('receipt')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'receipt'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      🧾 Receipt
                    </button>
                  </nav>
                </div>
                
                {/* Tab Content */}
                <div 
                  className="border rounded-lg p-4 sm:p-6 bg-gray-50"
                  style={{ height: 'calc(100vh - 300px)', minHeight: '500px', overflowY: 'auto' }}
                >
                  {activeTab === 'agreement' ? (
                    agreementHTML ? (
                      <div dangerouslySetInnerHTML={{ __html: agreementHTML }} />
                    ) : (
                      <div className="text-center text-gray-500 py-12">
                        <LoadingSpinner size="sm" />
                        <p className="mt-2">Loading agreement preview...</p>
                      </div>
                    )
                  ) : (
                    receiptHTML ? (
                      <div dangerouslySetInnerHTML={{ __html: receiptHTML }} />
                    ) : (
                      <div className="text-center text-gray-500 py-12">
                        <LoadingSpinner size="sm" />
                        <p className="mt-2">Loading receipt preview...</p>
                      </div>
                    )
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500 py-12">
                Select a draft order to preview documents
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
